/// Web Audio playback for IRPCS Rule 35 fog signals.
///
/// Two timbres are needed: a low ship's horn for prolonged/short
/// blasts, and a bell tremolo for vessels at anchor. We schedule
/// AudioParam gain ramps to gate a single oscillator for the horn,
/// and use rapid amplitude modulation on a higher carrier to fake
/// a bell.
///
/// Frequencies are deliberately lower than the Morse 700 Hz —
/// a real ship's horn sits around 200–300 Hz, and the lower pitch
/// is what makes a fog signal recognisable as a fog signal.

import type { FogPattern } from "./code";

export interface PlayFogOptions {
  /// Inject your own AudioContext (for tests). Defaults to a new
  /// `AudioContext` on first call.
  audioContext?: AudioContext;
  /// Peak gain on each "on" segment, 0–1. Default 0.25.
  gain?: number;
}

export interface FogPlayback {
  /// Resolves when the pattern finishes playing.
  done: Promise<void>;
  /// Stop playback immediately. Idempotent.
  stop: () => void;
}

const HORN_HZ = 240;
const BELL_HZ = 880;
const HORN_PROLONGED_S = 5; // IRPCS 32(c): "4 to 6 seconds"
const HORN_SHORT_S = 1; // IRPCS 32(b): "about 1 second"
const BELL_S = 5; // IRPCS 35(g): "ringing of the bell rapidly for about 5 seconds"
const GAP_S = 1.5; // between elements
const RAMP_S = 0.01;

/// Play a fog pattern once. The cycleSeconds field is informational
/// only — the game plays a single cycle so the player can listen and
/// answer without a metronome droning behind them.
export function playFog(
  pattern: FogPattern,
  options: PlayFogOptions = {},
): FogPlayback {
  const ctx = options.audioContext ?? new AudioContext();
  const peak = options.gain ?? 0.25;
  void ctx.resume();

  const nodes: { stop: (when: number) => void }[] = [];
  let cursor = ctx.currentTime + 0.05;
  let lastEnd = cursor;

  for (let i = 0; i < pattern.elements.length; i++) {
    const el = pattern.elements[i]!;
    if (i > 0) cursor += GAP_S;
    if (el === "P") {
      lastEnd = scheduleHorn(ctx, cursor, HORN_PROLONGED_S, peak, nodes);
      cursor = lastEnd;
    } else if (el === "S") {
      lastEnd = scheduleHorn(ctx, cursor, HORN_SHORT_S, peak, nodes);
      cursor = lastEnd;
    } else {
      lastEnd = scheduleBell(ctx, cursor, BELL_S, peak, nodes);
      cursor = lastEnd;
    }
  }

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    const now = ctx.currentTime;
    for (const n of nodes) {
      try {
        n.stop(now);
      } catch {
        /* already stopped */
      }
    }
  };

  const done = new Promise<void>((resolve) => {
    const ms = Math.max(0, (lastEnd - ctx.currentTime) * 1000) + 50;
    setTimeout(resolve, ms);
  });

  return { done, stop };
}

function scheduleHorn(
  ctx: AudioContext,
  startSec: number,
  durationSec: number,
  peak: number,
  nodes: { stop: (when: number) => void }[],
): number {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sawtooth"; // richer harmonics — sounds more like a horn
  osc.frequency.value = HORN_HZ;
  osc.connect(gain).connect(ctx.destination);
  const end = startSec + durationSec;
  gain.gain.setValueAtTime(0, startSec);
  gain.gain.linearRampToValueAtTime(peak, startSec + RAMP_S);
  gain.gain.setValueAtTime(peak, end - RAMP_S);
  gain.gain.linearRampToValueAtTime(0, end);
  osc.start(startSec);
  osc.stop(end + 0.05);
  nodes.push({ stop: (when) => osc.stop(when) });
  return end;
}

function scheduleBell(
  ctx: AudioContext,
  startSec: number,
  durationSec: number,
  peak: number,
  nodes: { stop: (when: number) => void }[],
): number {
  // Rapid "ding ding ding" — 5 strikes per second, each ~80 ms with
  // a fast decay envelope so it reads as a bell not a tone.
  const STRIKES_PER_SEC = 5;
  const STRIKE_S = 0.18;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.value = BELL_HZ;
  osc.connect(gain).connect(ctx.destination);
  gain.gain.setValueAtTime(0, startSec);

  const totalStrikes = Math.floor(durationSec * STRIKES_PER_SEC);
  for (let i = 0; i < totalStrikes; i++) {
    const t = startSec + i / STRIKES_PER_SEC;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(peak, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + STRIKE_S);
  }
  const end = startSec + durationSec;
  gain.gain.setValueAtTime(0, end);
  osc.start(startSec);
  osc.stop(end + 0.05);
  nodes.push({ stop: (when) => osc.stop(when) });
  return end;
}
